import { Button as MuiButton, type ButtonProps as MuiButtonProps } from "@mui/material";

import { ButtonInternalLink } from "./ButtonInternalLink";
import { Icon, type IconSize, type IconType } from "./Icon";

// Define custom variants and sizes
export const BUTTON_VARIANTS = [
  "primary",
  "secondary",
  "outlined",
  "muted",
  "link",
  "linkInverted",
  "outlineInverted",
  "danger",
] as const;

export const BUTTON_SIZES = ["small", "medium", "large"] as const;

export type ButtonVariant = typeof BUTTON_VARIANTS[number];
export type ButtonSize = typeof BUTTON_SIZES[number];

export interface ButtonProps
  extends Omit<MuiButtonProps, "variant" | "color" | "startIcon" | "endIcon"> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  LinkComponent?: React.ElementType;
  startIconType?: IconType;
  endIconType?: IconType;
}

const iconSizeMap: Record<ButtonSize, IconSize> = {
  small: "small",
  medium: "medium",
  large: "large",
};

/**
 * A wrapper around MuiButton that limits variants to our custom ones and adds our custom icon
 * handling.
 *
 * NOTE: Provide `href` to make the button render as an anchor tag. It will support react-router
 * redirects.
 */
export function Button(props: ButtonProps) {
  const {
    variant = "primary",
    LinkComponent = ButtonInternalLink,
    startIconType,
    endIconType,
    size = "medium",
    sx,
    ...restProps
  } = props;

  return (
    <MuiButton
      {...restProps}
      variant={variant}
      size={size}
      LinkComponent={LinkComponent}
      startIcon={startIconType ? <Icon type={startIconType} size={iconSizeMap[size]} /> : undefined}
      endIcon={endIconType ? <Icon type={endIconType} size={iconSizeMap[size]} /> : undefined}
      sx={sx}
    />
  );
}
